import React, { useState, useEffect } from 'react';
import { Text, View } from 'react-native';
import { JsonForms } from '@jsonforms/react';
import { StyledModal, StyledModalButtonProps } from '../StyledModal.tsx';
import ErrorBoundary from '../errorBoundaries/ErrorBoundary.tsx';
import { StateProgramSubmission } from './StateProgramSubmission.tsx';
import { useLocalization } from '../../store/utils/useLocalization';
import { useStateProgramContext } from './StateProgramContext.tsx';
import StateProgramFacade from './StateProgramFacade';
import { StateProgramFormStep } from './StateProgramFormStep.tsx';
import { logError } from '../../utils/logger';
import StateProgramEligibilityModalContent from './StateProgramEligibilityModalContent.tsx';
import StateProgramsError from './errors/StateProgramsError';

interface StateProgramWizardProps {
  isVisible: boolean;
  onClose: () => void;
  userId: number;
  visitId: number;
  providerId: number;
  formFactory: typeof StateProgramFormStep;
}

enum WizardStage {
  FORM_STEP = 'FORM_STEP',
  CONFIRMATION_DIALOG = 'CONFIRMATION_DIALOG',
  ELIGIBILITY_CHECK = 'ELIGIBILITY_CHECK',
  SUBMITTING = 'SUBMITTING',
  SUBMISSION_COMPLETE = 'SUBMISSION_COMPLETE',
  ERROR = 'ERROR',
}

/**
 * Configuration for each stage of the wizard
 */
interface WizardStageConfig {
  getContent: () => React.ReactNode;
  getButtons: () => StyledModalButtonProps[];
}

// Pre-defined content renderers to avoid React warnings about unstable components
const renderEligibilityContent = (programServices: StateProgramFacade): React.ReactNode => (
  <StateProgramEligibilityModalContent
    eligibilityEvaluation={programServices.getEligibility()}
    programName={programServices.name}
  />
);

const renderConfirmationContent = (
  programServices: StateProgramFacade,
  t: (key: string, params?: any) => string
): React.ReactNode => (
  <View>
    <Text>{t('state_program_cancel_confirmation', { programName: programServices.name })}</Text>
  </View>
);

const renderFormContent = (
  FormComponent: typeof StateProgramFormStep,
  currentStepKey: string,
  formUpdated: () => void
): React.ReactNode => <FormComponent formDelegate={JsonForms} key={currentStepKey} onChange={formUpdated} />;

const renderSubmissionContent = (
  userId: number,
  visitId: number,
  providerId: number,
  handleSubmissionComplete: () => void
): React.ReactNode => (
  <StateProgramSubmission
    userId={userId}
    visitId={visitId}
    providerId={providerId}
    onSubmissionComplete={handleSubmissionComplete}
  />
);

// eslint-disable-next-line import/prefer-default-export
export function StateProgramWizard(props: StateProgramWizardProps): React.ReactNode {
  const { t } = useLocalization();
  const programServices = useStateProgramContext();
  const [stage, setStage] = useState<WizardStage>(WizardStage.FORM_STEP);
  const [hasValidationError, setHasValidationError] = useState(false);
  const [currentStepKey, setCurrentStepKey] = useState(programServices.currentStepId);
  const [isModalActive, setIsModalActive] = useState(false);

  // Reset wizard to first step whenever it becomes visible
  useEffect(() => {
    if (props.isVisible) {
      // Hide modal during reset to prevent showing wrong step
      setIsModalActive(false);

      try {
        programServices.goToFirstStep();
        setCurrentStepKey(programServices.currentStepId);
        setStage(WizardStage.FORM_STEP);
      } catch (error) {
        // Log the error with context for monitoring
        logError(
          new StateProgramsError(
            'Failed to reset wizard to first step',
            error,
            programServices.id,
            undefined,
            props.visitId,
            props.providerId
          ),
          { currentStepId: programServices.currentStepId }
        );
      } finally {
        // Show modal after reset (whether we made it to first step or not)
        setIsModalActive(true);
      }
    } else if (!props.isVisible) {
      // Hide modal when component is told to hide
      setIsModalActive(false);

      // Reset the wizard state when closing the modal
      setStage(WizardStage.FORM_STEP);
    }
  }, [props.isVisible, programServices.id]);

  const calculateEligibility = () => {
    setStage(WizardStage.ELIGIBILITY_CHECK);
  };

  const startSubmission = () => {
    // Start the submission process
    setStage(WizardStage.SUBMITTING);
  };

  const goToNextStep = () => {
    try {
      programServices.goToNextStep();
      setCurrentStepKey(programServices.currentStepId); // trigger re-render if changed
      setHasValidationError(false); // Clear validation error on successful navigation
    } catch (cause) {
      // Log navigation errors from the facade (both validation and unexpected errors)
      // TODO: Refactor so this error causes the user to see an error screen
      // rather than just not being able to go to the next step
      const error = new StateProgramsError(
        'Failed to navigate to next step',
        cause,
        programServices.id,
        programServices.version,
        props.visitId,
        props.providerId
      );
      logError(error, { currentStepKey });
      setHasValidationError(true);
    }
  };

  const goToPreviousStep = () => {
    try {
      programServices.goToPreviousStep();
      setCurrentStepKey(programServices.currentStepId); // trigger re-render if changed
      setHasValidationError(false); // Clear validation error on successful navigation
    } catch (cause) {
      // TODO: Refactor so this error causes the user to see an error screen
      // rather than just not being able to go to the previous step
      const error = new StateProgramsError(
        'Failed to navigate to next step',
        cause,
        programServices.id,
        programServices.version,
        props.visitId,
        props.providerId
      );
      logError(error, { currentStepKey });
      setHasValidationError(true); // Set validation error state for UI
    }
  };

  const closeWizard = () => {
    // Call onClose first to trigger modal hiding, then clear state in useEffect
    props.onClose();
  };

  const handleSubmissionComplete = () => {
    setStage(WizardStage.SUBMISSION_COMPLETE);
  };

  const showCancelConfirmation = () => {
    setStage(WizardStage.CONFIRMATION_DIALOG);
  };

  const resumeAttestation = () => {
    setStage(WizardStage.FORM_STEP);
    // Just hide the dialog, stay on current step
  };

  const formUpdated = () => {
    setHasValidationError(!programServices.isStepUpdateSuccessful);
  };

  // Define stage configurations with content and buttons for each stage
  const stageConfigs: Record<WizardStage, WizardStageConfig> = {
    [WizardStage.ELIGIBILITY_CHECK]: {
      getContent: () => renderEligibilityContent(programServices),
      getButtons: () => [
        {
          testID: `StateProgramWizard-${programServices.id}-cancelAttestationButton`,
          onPress: closeWizard,
          localeKey: 'state_program_cancel_attestation',
          filled: false,
        },
        {
          testID: `StateProgramWizard-${programServices.id}-submitButton`,
          onPress: startSubmission,
          localeKey: 'app_button_submit',
          filled: true,
          disabled: hasValidationError,
        },
      ],
    },

    [WizardStage.CONFIRMATION_DIALOG]: {
      getContent: () => renderConfirmationContent(programServices, t),
      getButtons: () => [
        {
          testID: `StateProgramWizard-${programServices.id}-cancelAttestationButton`,
          onPress: closeWizard,
          localeKey: 'state_program_cancel_attestation',
          filled: false,
        },
        {
          testID: `StateProgramWizard-${programServices.id}-resumeAttestationButton`,
          onPress: resumeAttestation,
          localeKey: 'state_program_resume_attestation',
          filled: true,
        },
      ],
    },

    [WizardStage.ERROR]: {
      // Error content is handled by the ErrorBoundary's fallback
      getContent: () => null,
      getButtons: () => [
        {
          testID: `StateProgramWizard-${programServices.id}-closeButton`,
          onPress: closeWizard,
          localeKey: 'app_button_close',
        },
      ],
    },

    [WizardStage.SUBMISSION_COMPLETE]: {
      getContent: () =>
        renderSubmissionContent(props.userId, props.visitId, props.providerId, handleSubmissionComplete),
      getButtons: () => [
        {
          testID: `StateProgramWizard-${programServices.id}-doneButton`,
          onPress: closeWizard,
          localeKey: 'app_button_done',
        },
      ],
    },

    [WizardStage.SUBMITTING]: {
      getContent: () =>
        renderSubmissionContent(props.userId, props.visitId, props.providerId, handleSubmissionComplete),
      getButtons: () => [], // No buttons during submission
    },

    [WizardStage.FORM_STEP]: {
      getContent: () => renderFormContent(props.formFactory, currentStepKey, formUpdated),
      getButtons: () => {
        const buttons: StyledModalButtonProps[] = [];

        // Navigation buttons
        if (programServices.isFirstStep) {
          // Step 1: show cancel button that triggers confirmation
          buttons.push({
            testID: `StateProgramWizard-${programServices.id}-cancelButton`,
            onPress: showCancelConfirmation,
            localeKey: 'app_button_cancel',
          });
        } else {
          // Step 2+: show back button
          buttons.push({
            testID: `StateProgramWizard-${programServices.id}-backButton`,
            onPress: goToPreviousStep,
            localeKey: 'app_button_back',
          });
        }

        if (programServices.isLastStep) {
          buttons.push({
            testID: `StateProgramWizard-${programServices.id}-submitButton`,
            onPress: calculateEligibility,
            localeKey: 'app_button_next',
            filled: true,
            disabled: hasValidationError,
          });
        } else {
          // Show Next button on non-final steps
          buttons.push({
            testID: `StateProgramWizard-${programServices.id}-nextButton`,
            onPress: goToNextStep,
            localeKey: 'app_button_next',
            filled: true,
            disabled: hasValidationError,
          });
        }

        return buttons;
      },
    },
  };

  // Get content and buttons for the current stage
  const modalContent = stageConfigs[stage].getContent();
  const currentButtons = stageConfigs[stage].getButtons();

  return (
    <StyledModal
      testID={`StateProgramWizard-${programServices.id}`}
      headerText={programServices.name}
      isVisible={isModalActive}
      onClose={stage === WizardStage.CONFIRMATION_DIALOG ? undefined : showCancelConfirmation}
      buttons={currentButtons}
    >
      <ErrorBoundary
        fallback={
          <Text>
            {
              // Show the retry/support message if ANY error happens once we've already started the attestation
              t('state_program_attestation_cannot_complete', { programName: programServices.name })
            }
          </Text>
        }
        onError={() => setStage(WizardStage.ERROR)}
      >
        {modalContent}
      </ErrorBoundary>
    </StyledModal>
  );
}
